import styled, { css } from 'styled-components';

import Nullable from 'packages/kakapo-ui-services/types/Nullable';
import Breakpoint from 'packages/kakapo-ui-kit/helpers/Breakpoint';

interface IProps {
	// Either internal or external persons. Will try to fetch avatar for internal persons with
	// present `id` field
	person?: Nullable<{
		id: Nullable<string>;
		firstName: string;
		lastName: string;
		avatar?: { source: { url: string } } | null;
	}>;

	// Use name directly if only fullname string is present
	name?: Nullable<string>;

	size?: PersonAvatarSize;
}

export enum PersonAvatarSize {
	Small = 28,
	Medium = 38,
	MediumLarge = 90,
	Large = 120,
}

const style = css`
	display: inline-block;
	color: ${(props) => props.theme.colors.white};
	background: ${(props) => props.theme.colors.primary};
	margin-bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	user-select: none;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		max-width: 80px;
		max-height: 80px;
	}
`;

const BlankElement = styled.div`
	${style}
`;

const LettersElement = styled.p`
	${style}
`;

const ImageElement = styled.img`
	${style}
`;

const PersonAvatar = ({ person, name, size = PersonAvatarSize.Small }: IProps) => {
	const dimensionsStyle = {
		width: size,
		height: size,
		// Border-radius 50% is supported in some browsers, but creates visual glitches in others
		// calculating it in JS is the safer option
		borderRadius: Math.round(size / 2),
		fontSize: size * 0.35,
	};

	if (person?.avatar?.source.url) {
		return (
			<ImageElement
				src={`${person.avatar.source.url}?d=${size * 2}x${size * 2}`}
				style={dimensionsStyle}
			/>
		);
	}

	if (person) {
		const fullName = person ? `${person.firstName} ${person.lastName}` : name ?? '';

		return (
			<LettersElement style={dimensionsStyle}>
				{fullName
					.split(' ')
					.map((part) => part[0])
					.filter((part) => part !== undefined)
					.filter((part) => part.match(/[a-zA-Z]/))
					.slice(0, 3)
					.join('')
					.replace(/[a-z]*$/g, '')}
			</LettersElement>
		);
	}

	// Without personshow blank loading element
	return <BlankElement style={dimensionsStyle} />;
};

export default PersonAvatar;
